import {GROUP_READ_ONLY, GROUP_STAFF, GROUP_SUPER, GROUP_USER} from "../services/ApiTypes";
import {makeAutoObservable} from "mobx";
import {AuthManager} from "./AuthManager";
import type AuthApi from "../services/AuthApi";
import type ProfileStore from "../stores/ProfileStore";
import {stores} from "../stores";
import type {User, UserDisplaySerializer} from "../services/classes/User";
import type {SitUserProfile} from "../sit/ApiTypes";
import type {AuthProvider} from "./AuthTypes";
import {environment} from "../env.ts";

/**
 * Authentication can be improved
 * - Add refresh tokens
 * - Add support for automatic login
 * - Test by external party
 */
export default class AuthStore {
    readonly authentication: AuthManager;

    readonly allUsers = new Map<number, UserDisplaySerializer>();

    constructor(
        private api: AuthApi,
        private profileStore: ProfileStore,
    ) {
        makeAutoObservable(this);
        this.authentication = new AuthManager(this.api);
    }

    get user(): User | undefined {
        return this.authentication.authenticatedUser
    }

    get token(): string {
        return this.authentication.mithraAuthToken?.access_token || '';
    }

    get sitUserProfile(): SitUserProfile | null {
        return this.authentication.authenticatedUser?.sit_user_profile || null;
    }

    get userId() {
        return this.user?.id;
    }

    getUserId(): number {
        const userId = this.userId;
        if (typeof userId !== 'number') {
            throw new Error('Could not find user_id');
        }
        return userId;
    }

    get displayUser(): string {
        if (!this.user) {
            return ''
        }

        const d = this.user;
        if (d?.first_name) {
            const lastName = this.profileStore.filterLastName(d.last_name || '');
            return lastName ? `${d.first_name} ${lastName}` : d.first_name;
        }
        return this.user.username || '' // The username is the fall-back
    }

    get email(): string {
        return this.user?.email || ''
    }

    get isMithraStaff(): boolean {
        if (this.user) {
            // noinspection SuspiciousTypeOfGuard
            if (typeof this.user.is_staff === 'boolean') {
                return this.user.is_staff;
            }
        }
        return false
    }

    get provider(): AuthProvider | null {
        return this.authentication.mithraAuthToken?.provider || null;
    }

    get isFrontEgg(): boolean {
        return this.provider === 'front_egg'
    }

    userHasGroup(group: string): boolean {
        return this.user?.groups?.includes(group) || false;
    }

    get hasEditPermission() {
        if (!this.user || this.user.groups === undefined || this.user.groups === null) {
            // When no user is set, or no groups are known, we assume the user is read-only
            return false;
        }
        return !this.userHasGroup(GROUP_READ_ONLY)
    }

    get canUpload() {
        // Upload is allowed for staff users at all times
        return this.isMithraStaff;
    }

    get isCustomerSuper(): boolean {
        return this.userHasGroup(GROUP_SUPER);
    }

    get isMithraStaffGroup(): boolean {
        if (typeof environment.isTestStaff === 'boolean') return environment.isTestStaff;
        return this.userHasGroup(GROUP_STAFF);
    }

    get customerRole(): string {
        if (!this.user) {
            return 'unknown user'
        }
        if (this.isMithraStaffGroup) {
            return 'staff';
        }
        if (!this.hasEditPermission) {
            return 'view only';
        }
        if (this.isCustomerSuper) {
            return 'super user';
        }
        if (this.userHasGroup(GROUP_USER)) {
            return 'user';
        }
        return String(this.user.groups)
    }

    public isLoggedIn(): boolean {
        return Boolean(this.user);
    }

    devopsSetGroups(groups: string[]) {
        const userId = this.userId;
        const http = stores.materializedApi.http;
        if (!userId) return;
        return http.post<User>(`/users/${userId}/devops_set_groups/`, {groups})
            .then(r => this.authentication.onChangeUserData(r.data))
    }

    loadUsers() {
        this.$loadUsers()
    }

    $loadUsers() {
        return stores.materializedApi.http.get<UserDisplaySerializer[]>(`/users/`,)
            .then(r => {
                this.allUsers.clear()
                r.data.forEach(u => {
                    this.allUsers.set(u.id, u)
                })
            })
            .catch(error => {
                console.error(error)
                this.allUsers.clear()
            })
    }
}
